package ui.promo

import io.kvision.core.AlignItems
import io.kvision.core.Color
import io.kvision.core.CssSize
import io.kvision.core.Display
import io.kvision.core.FontWeight
import io.kvision.core.JustifyContent
import io.kvision.core.UNIT
import io.kvision.core.onEvent
import io.kvision.core.onInput
import io.kvision.form.text.textInput
import io.kvision.html.Autocomplete
import io.kvision.html.Button
import io.kvision.html.InputType
import io.kvision.html.P
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h3
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.HPanel
import io.kvision.panel.SimplePanel
import io.kvision.panel.VPanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.ttt.autogenesis.kvisionapp.ServerExtendBridge
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.RpcJson
import structs.accelbyte.promo.PromoCodeAvailability
import structs.accelbyte.promo.PromoCodeInfoResponse
import structs.accelbyte.promo.RedeemPromoCodeRequest
import structs.accelbyte.promo.RedeemPromoCodeResponse
import ui.billing.BillingOverlayWindow

/**
 * Player-facing promo code redemption modal.
 *
 * The widget renders one of six [State] values depending on the current
 * phase of the redemption flow:
 *
 * 1. [State.IDLE] — initial render. Input + CHECK + CANCEL.
 * 2. [State.CHECKING] — `getPromoCodeInfo` RPC in flight. Throbber + dim
 *    preview area.
 * 3. [State.PREVIEW_AVAILABLE] — server confirmed the code is still
 *    redeemable. Shows outcome type + the granted item card + REDEEM button.
 * 4. [State.PREVIEW_REDEEMED] — server says the code has already been
 *    redeemed. REDEEM button disabled.
 * 5. [State.PREVIEW_UNKNOWN] — server returned UNKNOWN (404 or malformed
 *    code). REDEEM button disabled; copy guides the player to retype.
 * 6. [State.REDEEMING] — `redeemPromoCode` RPC in flight. Throbber over
 *    the action row; the operator specifically called this out as a
 *    required affordance so the player knows the backend is doing work.
 * 7. [State.SUCCESS] — redemption succeeded. Green-bordered card with
 *    the granted item + a DONE button.
 * 8. [State.ERROR] — RPC failed. Error banner + RETRY button.
 *
 * The CHECK action calls [checkCode]; the REDEEM action calls
 * [submitRedemption]. Both go through [ServerExtendBridge.withTemporaryConnection]
 * so the client connects on demand and tears down after the call completes,
 * mirroring the [ui.billing.BillingState] pattern.
 *
 * The button in [ui.MainMenu] is intentionally STATELESS — we do not track
 * per-user redemption state on the client. The server is the source of
 * truth for whether a specific code is AVAILABLE.
 */
class PromoCodeOverlay : BillingOverlayWindow(
    modalTitle = "PROMO CODE",
    modalWidth = 520,
    modalMaxHeight = 540,
    extraClassName = "promo-modal"
)
{
    /**
     * Visible state of the redemption flow. Transitions are driven by
     * [renderState] — every transition goes through that one method so
     * the DOM updates are deterministic.
     */
    private enum class State
    {
        IDLE,
        CHECKING,
        PREVIEW_AVAILABLE,
        PREVIEW_REDEEMED,
        PREVIEW_UNKNOWN,
        REDEEMING,
        SUCCESS,
        ERROR
    }

    private val root: VPanel = vPanel(spacing = 16)
    private val subtitle: P = p("")
    private val input = textInput(type = InputType.TEXT) {
        placeholder = "ENTER CODE"
        this.autocomplete = Autocomplete.OFF
        setAttribute("spellcheck", "false")
    }
    private val checkButton: Button = button("Check")
    private val cancelButton: Button = button("Cancel")
    private val redeemButton: Button = button("Redeem")
    private val retryButton: Button = button("Retry")

    private val previewBlock: SimplePanel = SimplePanel()
    private val actionRow: HPanel = hPanel(spacing = 12, alignItems = AlignItems.CENTER, justify = JustifyContent.FLEXEND)
    private val successCard: SimplePanel = SimplePanel()
    private val errorBanner: SimplePanel = SimplePanel()

    private var currentState: State = State.IDLE

    /** Last value the user typed; cached so the REDEEM action can resubmit. */
    private var lastCheckedCode: String = ""

    init
    {
        addCssClass("promo-overlay")
        initContent()
    }

    override fun buildContent(host: VPanel)
    {
        root.width = CssSize(100, UNIT.perc)
        root.padding = CssSize(0, UNIT.px)

        subtitle.addCssClass("promo-subtitle")
        subtitle.content = "Enter your single-use code below. We'll show you what it grants before you commit."

        input.addCssClass("promo-input")
        input.width = CssSize(100, UNIT.perc)
        input.maxlength = 64
        input.onInput {
            // Soft reset preview when the user edits the code after a check
            if (currentState == State.PREVIEW_AVAILABLE ||
                currentState == State.PREVIEW_REDEEMED ||
                currentState == State.PREVIEW_UNKNOWN) {
                renderState(State.IDLE)
            }
        }
        input.onEvent {
            keydown = { event ->
                val key = event.asDynamic().key as? String
                if (key == "Enter" &&
                    (currentState == State.IDLE || currentState == State.ERROR)) {
                    triggerCheck()
                }
            }
        }

        checkButton.addCssClass("btn")
        checkButton.addCssClass("btn-promo-check")
        checkButton.onClick { triggerCheck() }

        cancelButton.addCssClass("btn")
        cancelButton.addCssClass("btn-promo-cancel")
        cancelButton.onClick { dismiss() }

        redeemButton.addCssClass("btn")
        redeemButton.addCssClass("btn-promo-redeem")
        redeemButton.onClick { triggerRedeem() }

        retryButton.addCssClass("btn")
        retryButton.addCssClass("btn-promo-retry")
        retryButton.onClick { triggerCheck() }

        previewBlock.addCssClass("promo-preview")
        previewBlock.width = CssSize(100, UNIT.perc)

        successCard.addCssClass("promo-success-card")
        successCard.width = CssSize(100, UNIT.perc)
        successCard.hide()

        errorBanner.addCssClass("promo-error-banner")
        errorBanner.width = CssSize(100, UNIT.perc)
        errorBanner.hide()

        val inputRow = hPanel(spacing = 12, alignItems = AlignItems.CENTER)
        inputRow.addCssClass("promo-input-row")
        inputRow.add(input)
        inputRow.add(checkButton)

        actionRow.addCssClass("promo-action-row")
        actionRow.add(cancelButton)
        actionRow.add(redeemButton)
        actionRow.add(retryButton)

        root.add(subtitle)
        root.add(inputRow)
        root.add(errorBanner)
        root.add(previewBlock)
        root.add(successCard)
        root.add(actionRow)

        host.add(root)
        renderState(State.IDLE)
    }

    /**
     * Rebuilds the visible state. The single source of truth for "what
     * does the user see right now" — every other method that wants to
     * change visible state calls this.
     */
    private fun renderState(state: State)
    {
        currentState = state
        Logger.debug(LogCategory.UI, "PromoCodeOverlay.renderState: state=$state")
        when (state) {
            State.IDLE -> renderIdle()
            State.CHECKING -> renderChecking()
            State.PREVIEW_AVAILABLE -> renderPreviewAvailable()
            State.PREVIEW_REDEEMED -> renderPreviewRedeemed()
            State.PREVIEW_UNKNOWN -> renderPreviewUnknown()
            State.REDEEMING -> renderRedeeming()
            State.SUCCESS -> renderSuccess()
            State.ERROR -> renderError()
        }
    }

    private fun renderIdle()
    {
        subtitle.content = "Enter your single-use code below. We'll show you what it grants before you commit."
        input.disabled = false
        input.value = ""
        checkButton.disabled = false
        checkButton.text = "Check"
        checkButton.show()
        cancelButton.hide()
        redeemButton.hide()
        retryButton.hide()
        previewBlock.removeAll()
        previewBlock.hide()
        successCard.hide()
        errorBanner.hide()
        actionRow.hide()
    }

    private fun renderChecking()
    {
        subtitle.content = "Checking code against the server…"
        input.disabled = true
        checkButton.disabled = true
        checkButton.text = "Checking…"
        checkButton.show()
        cancelButton.hide()
        redeemButton.hide()
        retryButton.hide()
        successCard.hide()
        errorBanner.hide()
        previewBlock.removeAll()
        previewBlock.show()
        previewBlock.add(renderThrobberRow("Looking up code…"))
        actionRow.hide()
    }

    private fun renderPreviewAvailable()
    {
        subtitle.content = "This code is available. Confirm below to apply the grant to your account."
        input.disabled = true
        checkButton.hide()
        cancelButton.text = "Cancel"
        cancelButton.show()
        redeemButton.disabled = false
        redeemButton.text = "Redeem"
        redeemButton.show()
        retryButton.hide()
        successCard.hide()
        errorBanner.hide()
        previewBlock.removeAll()
        previewBlock.show()
        previewBlock.addCssClass("promo-preview-available")
        previewBlock.removeCssClass("promo-preview-redeemed")
        previewBlock.removeCssClass("promo-preview-unknown")
        actionRow.show()
    }

    private fun renderPreviewRedeemed()
    {
        subtitle.content = "This code has already been redeemed. Single-use codes can only be claimed once."
        input.disabled = false
        checkButton.disabled = false
        checkButton.text = "Check another"
        checkButton.show()
        cancelButton.text = "Close"
        cancelButton.show()
        redeemButton.disabled = true
        redeemButton.text = "Redeem"
        redeemButton.hide()
        retryButton.hide()
        successCard.hide()
        errorBanner.hide()
        previewBlock.removeAll()
        previewBlock.show()
        previewBlock.removeCssClass("promo-preview-available")
        previewBlock.addCssClass("promo-preview-redeemed")
        previewBlock.removeCssClass("promo-preview-unknown")
        actionRow.hide()
    }

    private fun renderPreviewUnknown()
    {
        subtitle.content = "We couldn't find that code. Double-check for typos and try again."
        input.disabled = false
        checkButton.disabled = false
        checkButton.text = "Check again"
        checkButton.show()
        cancelButton.text = "Close"
        cancelButton.show()
        redeemButton.disabled = true
        redeemButton.text = "Redeem"
        redeemButton.hide()
        retryButton.hide()
        successCard.hide()
        errorBanner.hide()
        previewBlock.removeAll()
        previewBlock.show()
        previewBlock.removeCssClass("promo-preview-available")
        previewBlock.removeCssClass("promo-preview-redeemed")
        previewBlock.addCssClass("promo-preview-unknown")
        actionRow.hide()
    }

    private fun renderRedeeming()
    {
        subtitle.content = "Redeeming… don't close this window."
        input.disabled = true
        checkButton.hide()
        cancelButton.disabled = true
        cancelButton.text = "Cancel"
        redeemButton.disabled = true
        redeemButton.text = "Redeeming…"
        retryButton.hide()
        successCard.hide()
        errorBanner.hide()
        previewBlock.removeAll()
        previewBlock.show()
        previewBlock.removeCssClass("promo-preview-available")
        previewBlock.removeCssClass("promo-preview-redeemed")
        previewBlock.removeCssClass("promo-preview-unknown")
        previewBlock.add(renderThrobberRow("Applying grant to your account…"))
        actionRow.hide()
    }

    private fun renderSuccess()
    {
        subtitle.content = "Code redeemed. The grant has been applied to your account."
        input.disabled = true
        checkButton.hide()
        cancelButton.hide()
        redeemButton.hide()
        retryButton.hide()
        previewBlock.hide()
        errorBanner.hide()
        successCard.show()
        actionRow.hide()
    }

    private fun renderError()
    {
        subtitle.content = "Something went wrong. Try again or check your network."
        input.disabled = false
        checkButton.disabled = false
        checkButton.text = "Check"
        checkButton.show()
        cancelButton.text = "Close"
        cancelButton.show()
        redeemButton.hide()
        retryButton.hide()
        previewBlock.hide()
        successCard.hide()
        errorBanner.show()
        actionRow.hide()
    }

    private fun renderThrobberRow(message: String): HPanel
    {
        val row = hPanel(spacing = 12, alignItems = AlignItems.CENTER)
        row.addCssClass("promo-throbber-row")
        val spinner = div("")
        spinner.addCssClass("promo-throbber")
        row.add(spinner)
        val label = span(message)
        label.addCssClass("promo-throbber-label")
        row.add(label)
        return row
    }

    /**
     * Populates the preview block for the AVAILABLE state. Called from
     * [triggerCheck] after the server returns a usable response.
     */
    private fun populateAvailablePreview(response: PromoCodeInfoResponse)
    {
        previewBlock.removeAll()
        val summary = vPanel(spacing = 6)
        summary.addCssClass("promo-preview-summary")

        val status = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        status.addCssClass("promo-preview-row")
        val statusLabel = span("Status")
        statusLabel.addCssClass("promo-preview-label")
        status.add(statusLabel)
        val statusValue = span("● AVAILABLE")
        statusValue.addCssClass("promo-status-ok")
        statusValue.addCssClass("promo-preview-value")
        status.add(statusValue)
        summary.add(status)

        val outcomeLabel = response.outcomeType?.toString() ?: "Unknown"
        val outcome = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        outcome.addCssClass("promo-preview-row")
        val outcomeLabelSpan = span("Outcome")
        outcomeLabelSpan.addCssClass("promo-preview-label")
        outcome.add(outcomeLabelSpan)
        val outcomeValueSpan = span(outcomeLabel)
        outcomeValueSpan.addCssClass("promo-preview-value")
        outcome.add(outcomeValueSpan)
        summary.add(outcome)

        previewBlock.add(summary)
    }

    /**
     * Populates the preview block for the REDEEMED state with a
     * "code already used" hint.
     */
    private fun populateRedeemedPreview()
    {
        previewBlock.removeAll()
        val summary = vPanel(spacing = 6)
        summary.addCssClass("promo-preview-summary")
        val status = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        status.addCssClass("promo-preview-row")
        val statusLabel = span("Status")
        statusLabel.addCssClass("promo-preview-label")
        status.add(statusLabel)
        val statusValue = span("● REDEEMED")
        statusValue.addCssClass("promo-status-used")
        statusValue.addCssClass("promo-preview-value")
        status.add(statusValue)
        summary.add(status)
        val hint = p("This code was claimed by another player already.")
        hint.addCssClass("promo-preview-hint")
        summary.add(hint)
        previewBlock.add(summary)
    }

    /**
     * Populates the preview block for the UNKNOWN state.
     */
    private fun populateUnknownPreview()
    {
        previewBlock.removeAll()
        val summary = vPanel(spacing = 6)
        summary.addCssClass("promo-preview-summary")
        val status = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        status.addCssClass("promo-preview-row")
        val statusLabel = span("Status")
        statusLabel.addCssClass("promo-preview-label")
        status.add(statusLabel)
        val statusValue = span("● UNKNOWN")
        statusValue.addCssClass("promo-status-bad")
        statusValue.addCssClass("promo-preview-value")
        status.add(statusValue)
        summary.add(status)
        val hint = p("Codes are case-insensitive. Whitespace is trimmed automatically.")
        hint.addCssClass("promo-preview-hint")
        summary.add(hint)
        previewBlock.add(summary)
    }

    /**
     * Populates the success card after [submitRedemption] returns
     * `AVAILABLE` with a non-empty `grantedItems` list.
     */
    private fun populateSuccessCard(response: RedeemPromoCodeResponse)
    {
        successCard.removeAll()
        val check = hPanel(spacing = 0, alignItems = AlignItems.CENTER, justify = JustifyContent.CENTER)
        val checkMark = span("✓")
        checkMark.addCssClass("promo-check-mark")
        check.add(checkMark)
        successCard.add(check)

        val title = h3("Redeemed!")
        title.addCssClass("promo-success-title")
        successCard.add(title)

        if (response.grantedItems.isNotEmpty()) {
            val item = response.grantedItems.first()
            val days = item.extraSubscriptionDays ?: 0
            val itemRow = hPanel(spacing = 12, alignItems = AlignItems.CENTER)
            itemRow.addCssClass("promo-grant-row")
            val icon = span(item.iconForItem())
            icon.addCssClass("promo-grant-icon")
            itemRow.add(icon)
            val itemText = vPanel(spacing = 2)
            itemText.addCssClass("promo-grant-text")
            val resolvedName: String = item.itemName.takeIf { it.isNotBlank() } ?: "Subscription grant"
            val itemName = span(resolvedName)
            itemName.addCssClass("promo-grant-name")
            itemText.add(itemName)
            val itemDescText: String = when {
                days > 0 -> "$days days added to your subscription"
                else -> "Granted to your account"
            }
            val itemDescSpan = span(itemDescText)
            itemDescSpan.addCssClass("promo-grant-desc")
            itemText.add(itemDescSpan)
            itemRow.add(itemText)
            successCard.add(itemRow)
        }

        val doneRow = hPanel(spacing = 0, alignItems = AlignItems.CENTER, justify = JustifyContent.CENTER)
        doneRow.addCssClass("promo-done-row")
        val done = button("Done")
        done.addCssClass("btn")
        done.addCssClass("btn-promo-done")
        done.onClick { dismiss() }
        doneRow.add(done)
        successCard.add(doneRow)
    }

    private fun populateErrorBanner(message: String)
    {
        errorBanner.removeAll()
        val banner = p(message)
        banner.addCssClass("promo-error-text")
        errorBanner.add(banner)
    }

    private fun triggerCheck()
    {
        val code = input.value?.trim() ?: ""
        if (code.isEmpty()) {
            populateErrorBanner("Enter a code first.")
            renderState(State.ERROR)
            return
        }
        lastCheckedCode = code
        renderState(State.CHECKING)
        scope.launch {
            try {
                val info = checkCode(code)
                when (info.availability) {
                    PromoCodeAvailability.AVAILABLE -> {
                        populateAvailablePreview(info)
                        renderState(State.PREVIEW_AVAILABLE)
                    }
                    PromoCodeAvailability.REDEEMED -> {
                        populateRedeemedPreview()
                        renderState(State.PREVIEW_REDEEMED)
                    }
                    PromoCodeAvailability.UNKNOWN -> {
                        populateUnknownPreview()
                        renderState(State.PREVIEW_UNKNOWN)
                    }
                    PromoCodeAvailability.REDEEMING,
                    PromoCodeAvailability.DISABLED,
                    PromoCodeAvailability.UNAUTHORIZED -> {
                        populateUnknownPreview()
                        renderState(State.PREVIEW_UNKNOWN)
                    }
                }
            } catch (err: Throwable) {
                Logger.warn(LogCategory.NETWORK, "PromoCodeOverlay: checkCode failed: ${err.message}")
                populateErrorBanner(err.message ?: "Check failed.")
                renderState(State.ERROR)
            }
        }
    }

    private fun triggerRedeem()
    {
        if (lastCheckedCode.isEmpty()) return
        renderState(State.REDEEMING)
        scope.launch {
            try {
                val response = submitRedemption(lastCheckedCode)
                when {
                    response.availability == PromoCodeAvailability.AVAILABLE &&
                        response.grantedItems.isNotEmpty() -> {
                        populateSuccessCard(response)
                        renderState(State.SUCCESS)
                    }
                    response.availability == PromoCodeAvailability.REDEEMED -> {
                        populateRedeemedPreview()
                        renderState(State.PREVIEW_REDEEMED)
                    }
                    else -> {
                        populateErrorBanner(response.errorMessage ?: "Redemption failed.")
                        renderState(State.ERROR)
                    }
                }
            } catch (err: Throwable) {
                Logger.warn(LogCategory.NETWORK, "PromoCodeOverlay: submitRedemption failed: ${err.message}")
                populateErrorBanner(err.message ?: "Redemption failed.")
                renderState(State.ERROR)
            }
        }
    }

    /**
     * Calls `server.extend.getPromoCodeInfo` for the typed code. The
     * response is server-pushed: AGS's `getCode(..., redeemable=true)`
     * populates `remainder` from `maxRedeemCountPerCode - redeemedCount`,
     * which the proxy maps into the `availability` enum.
     */
    private suspend fun checkCode(value: String): PromoCodeInfoResponse
    {
        val request = RedeemPromoCodeRequest(codeValue = value)
        return ServerExtendBridge.withTemporaryConnection {
            val invoker = ServerExtendBridge.rpcInvoker
                ?: error("ServerExtendBridge has no invoker (not connected)")
            val response = invoker.invoke(
                "server.extend.getPromoCodeInfo",
                request,
                RedeemPromoCodeRequest.serializer()
            )
            val error = response.error
            if (error != null) {
                throw IllegalStateException("getPromoCodeInfo error: ${error.message}")
            }
            response.result?.let {
                RpcJson.decodeFromJsonElement(PromoCodeInfoResponse.serializer(), it)
            } ?: throw IllegalStateException("getPromoCodeInfo returned no result")
        }
    }

    /**
     * Calls `server.extend.redeemPromoCode` for the previously-checked code.
     * The proxy pins `userId` from `RpcCallContext` — the client never
     * sends one. The response carries `grantedItems` + `persistedAddOns`
     * for the success card.
     */
    private suspend fun submitRedemption(code: String): RedeemPromoCodeResponse
    {
        val request = RedeemPromoCodeRequest(codeValue = code)
        return ServerExtendBridge.withTemporaryConnection {
            val invoker = ServerExtendBridge.rpcInvoker
                ?: error("ServerExtendBridge has no invoker (not connected)")
            val response = invoker.invoke(
                "server.extend.redeemPromoCode",
                request,
                RedeemPromoCodeRequest.serializer()
            )
            val error = response.error
            if (error != null) {
                throw IllegalStateException("redeemPromoCode error: ${error.message}")
            }
            response.result?.let {
                RpcJson.decodeFromJsonElement(RedeemPromoCodeResponse.serializer(), it)
            } ?: throw IllegalStateException("redeemPromoCode returned no result")
        }
    }

    private fun structs.accelbyte.promo.PromoCodeRedeemedItem.iconForItem(): String
    {
        val days: Int = extraSubscriptionDays ?: 0
        if (days > 0) return "👑"
        val id = itemId
        if (id.contains("EMPIRE", ignoreCase = true)) return "🏛"
        if (id.contains("REPUBLIC", ignoreCase = true)) return "⚖"
        if (id.contains("JUNTA", ignoreCase = true)) return "✯"
        return "🎁"
    }
}
